import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Building2, MapPin, Globe, Plus, Package, Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface Specification {
  label: string;
  value: string;
}

interface Distributor {
  id: string;
  name: string;
  location: string;
  region: string;
  isPreferred?: boolean;
}

interface ProductQuickViewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: {
    id: string;
    title: string;
    description?: string | null;
    category?: string | null;
    image?: string | null;
    specifications?: Specification[] | Record<string, string> | null;
    keyFeatures?: string[];
    distributors?: Distributor[];
  } | null;
  onAddToEnquiry?: () => void;
  isInEnquiry?: boolean;
}

const categoryColors: Record<string, { bg: string; text: string }> = {
  valves: { bg: "bg-slate-700", text: "text-white" },
  hydraulics: { bg: "bg-amber-600", text: "text-white" },
  electronics: { bg: "bg-emerald-600", text: "text-white" },
  robotics: { bg: "bg-blue-600", text: "text-white" },
  services: { bg: "bg-purple-600", text: "text-white" },
  sensors: { bg: "bg-cyan-600", text: "text-white" },
  motors: { bg: "bg-rose-600", text: "text-white" },
  controllers: { bg: "bg-indigo-600", text: "text-white" },
  actuators: { bg: "bg-teal-600", text: "text-white" },
  power: { bg: "bg-orange-600", text: "text-white" },
  connectors: { bg: "bg-violet-600", text: "text-white" },
  networking: { bg: "bg-sky-600", text: "text-white" },
  other: { bg: "bg-gray-600", text: "text-white" },
};

function getCategoryStyle(category: string | null | undefined) {
  if (!category) return categoryColors.other;
  const key = category.toLowerCase().replace(/[^a-z]/g, "");
  return categoryColors[key] || categoryColors.other;
}

function normalizeSpecs(specs: Specification[] | Record<string, string> | null | undefined): Specification[] {
  if (!specs) return [];
  if (Array.isArray(specs)) return specs;
  return Object.entries(specs).map(([label, value]) => ({ label, value }));
}

const regions = [
  { value: "north-america", label: "North America" },
  { value: "europe", label: "Europe" },
  { value: "asia-pacific", label: "Asia Pacific" },
  { value: "middle-east", label: "Middle East" },
  { value: "latin-america", label: "Latin America" },
];

const sampleDistributors: Distributor[] = [
  { id: "1", name: "Global Industrial Supply", location: "New York, NY", region: "north-america", isPreferred: true },
  { id: "2", name: "Southern Mech", location: "Houston, TX", region: "north-america" },
  { id: "3", name: "TechParts Direct", location: "Chicago, IL", region: "north-america" },
  { id: "4", name: "European Automation", location: "Munich, Germany", region: "europe", isPreferred: true },
  { id: "5", name: "Nordic Components", location: "Stockholm, Sweden", region: "europe" },
  { id: "6", name: "Asia Tech Solutions", location: "Singapore", region: "asia-pacific", isPreferred: true },
  { id: "7", name: "Pacific Industrial", location: "Sydney, Australia", region: "asia-pacific" },
];

export function ProductQuickViewModal({
  open,
  onOpenChange,
  product,
  onAddToEnquiry,
  isInEnquiry = false,
}: ProductQuickViewModalProps) {
  const [selectedRegion, setSelectedRegion] = useState("north-america");

  if (!product) return null;

  const categoryStyle = getCategoryStyle(product.category);
  const specs = normalizeSpecs(product.specifications);
  
  const distributors = product.distributors?.length 
    ? product.distributors 
    : sampleDistributors;
  
  const filteredDistributors = distributors.filter(d => d.region === selectedRegion);

  const keyFeatures = product.keyFeatures || [
    "Zero-leakage seat design",
    "Corrosion-resistant coating",
    "Field-replaceable internals",
    "SIL 3 Certified",
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden">
        <div className="flex flex-col md:flex-row">
          <div className="relative w-full md:w-2/5 bg-muted/30">
            {product.category && (
              <Badge 
                className={cn(
                  "absolute top-3 left-3 z-10 px-2.5 py-0.5 text-xs font-medium",
                  categoryStyle.bg,
                  categoryStyle.text
                )}
              >
                {product.category}
              </Badge>
            )}
            <div className="aspect-square flex items-center justify-center p-6">
              {product.image ? (
                <img
                  src={product.image}
                  alt={product.title}
                  className="h-full w-full object-contain"
                />
              ) : (
                <Package className="h-24 w-24 text-muted-foreground/30" />
              )}
            </div>
          </div>

          <div className="flex-1 p-6">
            <DialogHeader className="pb-4">
              <DialogTitle className="text-xl font-bold leading-tight">
                {product.title}
              </DialogTitle>
            </DialogHeader>

            {product.description && (
              <p className="text-sm text-muted-foreground mb-5">
                {product.description}
              </p>
            )}

            <div className="mb-5 rounded-lg border bg-muted/20 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  Available Distributors
                </div>
                <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                  <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="select-region">
                    <Globe className="h-3 w-3 mr-1" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((region) => (
                      <SelectItem key={region.value} value={region.value}>
                        {region.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                {filteredDistributors.length > 0 ? (
                  filteredDistributors.map((distributor) => (
                    <div
                      key={distributor.id}
                      className="flex items-center justify-between py-2 border-b last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{distributor.name}</span>
                        {distributor.isPreferred && (
                          <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                            <Star className="h-2.5 w-2.5 mr-0.5 fill-current" />
                            Preferred
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {distributor.location}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground py-2">
                    No distributors available in this region.
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-5">
              {specs.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Specifications
                  </h4>
                  <ul className="space-y-1.5">
                    {specs.slice(0, 5).map((spec, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <span className="text-orange-500 mt-1.5">•</span>
                        <span>
                          <span className="font-medium">{spec.label}:</span>{" "}
                          {spec.value}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  Key Features
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {keyFeatures.map((feature, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="text-xs font-normal"
                    >
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            <Button
              variant="default"
              className={cn(
                "w-full gap-1.5 font-medium",
                isInEnquiry 
                  ? "bg-green-600 border-green-700 text-white"
                  : "bg-orange-500 border-orange-600 text-white"
              )}
              onClick={onAddToEnquiry}
              data-testid="button-modal-add-enquiry"
            >
              <Plus className="h-4 w-4" />
              {isInEnquiry ? "Added to Enquiry" : "Add to Enquiry"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
